"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { ImageOff, Play } from "lucide-react";
import {
  motion,
  useMotionTemplate,
  useReducedMotion,
  useSpring,
} from "motion/react";

import { youtubeId, type ProductMediaItem } from "@/lib/product-media";
import { cn } from "@/lib/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

import styles from "./product-detail.module.css";

function GalleryImage({
  url,
  caption,
  priority = false,
}: {
  url: string;
  caption: string;
  priority?: boolean;
}) {
  const [failed, setFailed] = useState(false);

  return failed ? (
    <div className="flex h-full items-center justify-center gap-2 text-sm text-zinc-400">
      <ImageOff className="size-5" />
      Image unavailable
    </div>
  ) : (
    <Image
      src={url}
      alt={caption}
      fill
      sizes="(min-width: 1024px) 800px, 100vw"
      className="object-cover"
      unoptimized
      priority={priority}
      onError={() => setFailed(true)}
    />
  );
}

export function ProductShowcase({
  title,
  mediaUrl,
  isVideo,
  gallery = [],
}: {
  title: string;
  mediaUrl: string;
  isVideo: boolean;
  gallery?: ProductMediaItem[];
}) {
  const reduced = useReducedMotion();
  const [api, setApi] = useState<CarouselApi>();
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(false);
  const pointerX = useSpring(50, { stiffness: 240, damping: 30 });
  const pointerY = useSpring(42, { stiffness: 240, damping: 30 });
  const spotlight = useMotionTemplate`radial-gradient(34rem circle at ${pointerX}% ${pointerY}%, rgb(196 181 253 / 0.16), transparent 58%)`;
  const items: ProductMediaItem[] = gallery.length
    ? gallery
    : mediaUrl
      ? [{ type: isVideo ? "video" : "image", url: mediaUrl, caption: title }]
      : [];
  const index = Math.min(selected, Math.max(0, items.length - 1));
  const active = items[index];

  useEffect(() => {
    if (!api) return;

    const syncSelection = () => {
      setSelected(api.selectedScrollSnap());
      setPlaying(false);
    };

    syncSelection();
    api.on("select", syncSelection);
    api.on("reInit", syncSelection);
    return () => {
      api.off("select", syncSelection);
      api.off("reInit", syncSelection);
    };
  }, [api]);

  function select(next: number) {
    setPlaying(false);
    setSelected(next);
    api?.scrollTo(next);
  }

  return (
    <section className={styles.gallery} aria-label="Product media gallery">
      <Carousel
        setApi={setApi}
        opts={{ align: "start", loop: false }}
        className={styles.mediaCarousel}
      >
        <CarouselContent className="ml-0">
          {items.length ? (
            items.map((item, itemIndex) => {
              const videoId =
                item.type === "youtube" ? youtubeId(item.url) : null;
              const isActive = itemIndex === index;

              return (
                <CarouselItem key={`${item.url}-${itemIndex}`} className="pl-0">
                  <div
                    className={styles.viewer}
                    onPointerMove={(event) => {
                      if (reduced || event.pointerType !== "mouse") return;
                      const box = event.currentTarget.getBoundingClientRect();
                      pointerX.set(
                        ((event.clientX - box.left) / box.width) * 100,
                      );
                      pointerY.set(
                        ((event.clientY - box.top) / box.height) * 100,
                      );
                    }}
                    onPointerLeave={() => {
                      pointerX.set(50);
                      pointerY.set(42);
                    }}
                  >
                    {item.type === "youtube" && videoId ? (
                      isActive && playing ? (
                        <iframe
                          className="h-full w-full border-0"
                          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`}
                          title={item.caption || `${title} video`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                        />
                      ) : (
                        <button
                          type="button"
                          className="group relative h-full w-full"
                          aria-label={`Play ${item.caption || "YouTube video"}`}
                          onClick={() => setPlaying(true)}
                        >
                          <GalleryImage
                            url={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
                            caption={item.caption || `${title} video`}
                          />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <span className="flex size-16 items-center justify-center rounded-full bg-white text-black shadow-xl transition-transform group-hover:scale-110">
                              <Play className="ml-1 size-7 fill-current" />
                            </span>
                          </span>
                        </button>
                      )
                    ) : item.type === "video" ? (
                      <video
                        src={item.url}
                        controls
                        playsInline
                        preload="metadata"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <GalleryImage
                        url={item.url}
                        caption={item.caption || title}
                        priority={itemIndex === 0}
                      />
                    )}
                    {!reduced && isActive && (
                      <motion.div
                        aria-hidden="true"
                        className={styles.viewerSpotlight}
                        style={{ background: spotlight }}
                      />
                    )}
                  </div>
                </CarouselItem>
              );
            })
          ) : (
            <CarouselItem className="pl-0">
              <div className={styles.viewer}>
                <div className="flex h-full items-center justify-center gap-2 text-sm text-zinc-400">
                  <ImageOff />
                  Product media coming soon
                </div>
              </div>
            </CarouselItem>
          )}
        </CarouselContent>
      </Carousel>

      {items.length > 0 && (
        <div className={styles.thumbnails} aria-label="Choose product media">
          {items.map((item, itemIndex) => (
            <button
              key={`${item.url}-thumbnail-${itemIndex}`}
              type="button"
              aria-label={`View media ${itemIndex + 1}: ${item.caption || item.type}`}
              aria-pressed={itemIndex === index}
              className={cn(
                styles.thumbnail,
                itemIndex === index && styles.selectedThumbnail,
              )}
              onClick={() => select(itemIndex)}
              onFocus={(event) =>
                event.currentTarget.scrollIntoView({
                  block: "nearest",
                  inline: "nearest",
                })
              }
            >
              {item.type === "video" ? (
                <span className="flex h-full items-center justify-center bg-zinc-900 text-white">
                  <Play size={20} />
                </span>
              ) : (
                <Image
                  src={
                    item.type === "youtube"
                      ? `https://i.ytimg.com/vi/${youtubeId(item.url)}/default.jpg`
                      : item.url
                  }
                  alt=""
                  fill
                  sizes="120px"
                  unoptimized
                  priority={itemIndex === 0}
                  className="object-cover"
                />
              )}
              {item.type === "youtube" && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/20 text-white">
                  <Play size={22} className="fill-current drop-shadow" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
